/**
 * PRS Analysis Dialog
 * Shows detailed analysis tabs for base PRS versions
 */

import React, {useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
} from '@mui/material';
import {Close as CloseIcon} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({children, value, index}) => {
  return (
    <div hidden={value !== index} style={{height: '100%'}}>
      {value === index && <Box sx={{p: 3, height: '100%', overflow: 'auto'}}>{children}</Box>}
    </div>
  );
};

interface PRSAnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  trialId: string;
  version: string;
  cachedAnalysis: any;
  cachedRequirements: any;
}

export const PRSAnalysisDialog: React.FC<PRSAnalysisDialogProps> = ({
  open,
  onClose,
  trialId,
  version,
  cachedAnalysis,
  cachedRequirements,
}) => {
  const [tabValue, setTabValue] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{sx: {height: '90vh'}}}
    >
      <DialogTitle>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography variant="h6">
            Detailed PRS Analysis - {trialId} v{version}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{p: 0, display: 'flex', flexDirection: 'column'}}>
        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{borderBottom: 1, borderColor: 'divider', px: 2}}
        >
          <Tab label="AI Analysis" />
          <Tab label="Study Schedule" />
          <Tab label="Requirements" />
          <Tab label="Version History" />
        </Tabs>

        {/* Tab 0: AI Analysis */}
        <TabPanel value={tabValue} index={0}>
          {cachedAnalysis ? (
            <Box
              sx={{
                '& h2': {fontSize: '1.5rem', fontWeight: 600, mb: 1.5, mt: 2},
                '& h3': {fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 2},
                '& p': {mb: 1.5, lineHeight: 1.7},
                '& ul, & ol': {mb: 2, pl: 3},
                '& li': {mb: 0.5},
                '& strong': {fontWeight: 600},
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {typeof cachedAnalysis === 'string'
                  ? cachedAnalysis
                  : cachedAnalysis.analysis_text || JSON.stringify(cachedAnalysis, null, 2)}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography color="text.secondary">No AI analysis available</Typography>
          )}
        </TabPanel>

        {/* Tab 1: Study Schedule */}
        <TabPanel value={tabValue} index={1}>
          <Typography color="text.secondary">Study Schedule coming soon</Typography>
        </TabPanel>

        {/* Tab 2: Requirements */}
        <TabPanel value={tabValue} index={2}>
          {cachedRequirements ? (
            <Box
              sx={{
                '& h2': {fontSize: '1.5rem', fontWeight: 600, mb: 1.5, mt: 2},
                '& h3': {fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 2},
                '& p': {mb: 1.5, lineHeight: 1.7},
                '& ul, & ol': {mb: 2, pl: 3},
                '& li': {mb: 0.5},
                '& strong': {fontWeight: 600},
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {typeof cachedRequirements.requirements === 'string'
                  ? cachedRequirements.requirements
                  : cachedRequirements.requirements?.requirements_text || JSON.stringify(cachedRequirements, null, 2)}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography color="text.secondary">No requirements available</Typography>
          )}
        </TabPanel>

        {/* Tab 3: Version History */}
        <TabPanel value={tabValue} index={3}>
          <Typography color="text.secondary">Version History coming soon</Typography>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{px: 3, py: 2}}>
        <Button onClick={onClose} variant="contained" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
